/**
 * «Riabbina in ordine di data» (2.23, decision of 20/10/2026): the payments of
 * a subject given again to his invoices, the oldest payment to the oldest
 * invoice, year after year. What is left of the payments waits on the
 * subject without a document (2.22). Only the payments of the banks already
 * on his invoices and those without a document move: credit notes,
 * compensations, closures, write-offs, collections kept by a bank and the
 * other subjects' parts of a movement stay as they are.
 */
import { z } from 'zod';
import { fromCents, toCents } from './money.js';
export const reallocationSchema = z.object({
    cycle: z.enum(['attivo', 'passivo']),
});
/**
 * The payments to the invoices in order of date (2.23): the oldest invoice
 * takes the oldest payment, as far as each needs; what is left of each
 * payment comes back as leftover. Invoices of the same day by their number.
 */
export function reallocateByDate(invoices, payments) {
    const numberOf = (value) => Number(/\d+/.exec(value)?.[0] ?? 0);
    const ordered = [...invoices].sort((a, b) => a.date.localeCompare(b.date) ||
        numberOf(a.number) - numberOf(b.number) ||
        a.number.localeCompare(b.number));
    const queue = [...payments]
        .sort((a, b) => a.date.localeCompare(b.date) || a.movementId.localeCompare(b.movementId))
        .map((payment) => ({ payment, left: toCents(payment.amount) }));
    const parts = new Map();
    const open = new Map();
    let at = 0;
    for (const invoice of ordered) {
        let need = toCents(invoice.toCover);
        const own = [];
        while (need > 0 && at < queue.length) {
            const current = queue[at];
            const taken = Math.min(current.left, need);
            if (taken > 0) {
                const { movementId, accountCode, card, date } = current.payment;
                own.push({ movementId, accountCode, card, date, amount: fromCents(taken) });
            }
            need -= taken;
            current.left -= taken;
            if (current.left === 0)
                at += 1;
        }
        parts.set(invoice.id, own);
        open.set(invoice.id, fromCents(Math.max(need, 0)));
    }
    const leftovers = queue
        .filter((item) => item.left > 0)
        .map(({ payment, left }) => ({
        movementId: payment.movementId,
        accountCode: payment.accountCode,
        card: payment.card,
        date: payment.date,
        amount: fromCents(left),
    }));
    return { parts, open, leftovers };
}
/** Where a part comes from, in words: «Carta •••• 4521», else the name of the account. */
export function reallocationSource(part, bankName) {
    return part.card ? `Carta •••• ${part.card}` : bankName(part.accountCode);
}
/** Two lists of parts are the same allocation: same movements with the same amounts. */
export function sameParts(a, b) {
    const key = (parts) => {
        const sums = new Map();
        for (const part of parts) {
            sums.set(part.movementId, (sums.get(part.movementId) ?? 0) + toCents(part.amount));
        }
        return [...sums.entries()]
            .filter(([, cents]) => cents !== 0)
            .sort(([x], [y]) => x.localeCompare(y))
            .map(([id, cents]) => `${id}:${cents}`)
            .join('|');
    };
    return key(a) === key(b);
}
//# sourceMappingURL=reallocation.js.map